// ##############################
// // // Function that converts from hex color to rgb color
// // // Example: input = #9c27b0 => output = 156, 39, 176
// // // Example: input = 9c27b0 => output = 156, 39, 176
// // // Example: input = #999 => output = 153, 153, 153
// // // Example: input = 999 => output = 153, 153, 153
// #############################
import { createMuiTheme } from '@material-ui/core/styles';
import { fontFamily, fontSize } from 'theme/fonts';

const hexToRgb = (input: string) => {
	input = input + '';
	input = input.replace('#', '');
	let hexRegex = /[0-9A-Fa-f]/g;
	if (!hexRegex.test(input) || (input.length !== 3 && input.length !== 6)) {
		throw new Error('input is not a valid hex color.');
	}
	if (input.length === 3) {
		let first = input[0];
		let second = input[1];
		let last = input[2];
		input = first + first + second + second + last + last;
	}
	input = input.toUpperCase();
	let first = input[0] + input[1];
	let second = input[2] + input[3];
	let last = input[4] + input[5];
	return parseInt(first, 16) + ', ' + parseInt(second, 16) + ', ' + parseInt(last, 16);
};

// ##############################
// // // Variables - Styles that are used on more than one component
// #############################

const drawerWidth = 260;

const drawerMiniWidth = 80;

const transition = {
	transition: 'all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)',
};

const containerFluid = {
	paddingRight: '15px',
	paddingLeft: '15px',
	marginRight: 'auto',
	marginLeft: 'auto',
	'&:before,&:after': {
		display: 'table',
		content: '" "',
	},
	'&:after': {
		clear: 'both',
	},
};

const container = {
	paddingRight: '15px',
	paddingLeft: '15px',
	marginRight: 'auto',
	marginLeft: 'auto',
	'@media (min-width: 768px)': {
		width: '750px',
	},
	'@media (min-width: 992px)': {
		width: '970px',
	},
	'@media (min-width: 1200px)': {
		width: '1170px',
	},
	'&:before,&:after': {
		display: 'table',
		content: '" "',
	},
	'&:after': {
		clear: 'both',
	},
};

const defaultFont = {
	fontFamily: '"FSAlvarRegular", "FSAlbertRegular", "Roboto", "Helvetica", "Arial", sans-serif',
	fontWeight: 'normal',
	lineHeight: '1.5em',
};

const defaultBodyFont = {
	...defaultFont,
	fontFamily: '"FSAlbertRegular", "FSAlvarRegular", "Roboto", "Helvetica", "Arial", sans-serif',
};

const primaryColor = ['#FF7800', '#FFBC80', '#FF861B', '#af2cc5', '#7b1fa2', '#FFD7B3', '#FFBC80'];
const secondaryColor = ['#FFD7B3', '#FFCD3A', ''];
const backgroundColor = ['#f4f4f4', '#FEEAE6'];
const warningColor = ['#FF7800', '#e90091', '#fb8c00', '#ffa21a', '#f57c00', '#faf2cc', '#fcf8e3'];
const dangerColor = ['#ce0058', '#ef5350', '#e53935', '#f55a4e', '#d32f2f', '#ebcccc', '#f2dede', '#F10044', '#AA1F01'];
const successColor = ['#00bfb3', '#00d5f4', '#43a047', '#5cb860', '#388e3c', '#d0e9c6', '#dff0d8'];
const infoColor = ['#004c97', '#007ac1', '#00acc1', '#00d3ee', '#0097a7', '#c4e3f3', '#d9edf7', '#fab4711f'];
const roseColor = ['#d943a2', '#ec407a', '#d81b60', '#eb3573', '#c2185b'];
const grayColor = [
	'#e0e0e0',
	'#f0f0f0',
	'#f5f5f5',
	'#fafafa',
	'#999',
	'#777',
	'#3C4858',
	'#AAAAAA',
	'#D2D2D2',
	'#DDD',
	'#555555',
	'#333',
	'#eee',
	'#ccc',
	'#e4e4e4',
	'#E5E5E5',
	'#f9f9f9',
	'#f5f5f5',
	'#495057',
	'#e7e7e7',
	'#212121',
	'#c8c8c8',
	'#505050',
	'#9E9E9E',
	//"#757575",
	'#efeeee6b',
	'#757575',
	'#F3F2F2',
	'#F1F1F1',
	'#323232',
	'#444444',
];
const grayColorShade_1 = '#757575';
const grayColorShade_2 = '#878787';
const grayColorShade_3 = '#A2A2A2';
const grayColorShade_4 = '#efeeee6b';
const grayColorShade_5 = '#f9f9f9';

const lilacColor = ['#E7EFFF', '#B5CEFF'];
const rowHoverColor = '#b1dff74f';
const infoColorLight = '#fab4711f';
const orangeLabelColor = '#FF7800';
const selectedRowColor = '#E5EDF5';
const unSelectedRowColor = '#F2F4F8';
const placeholderColor = '#a2a2a2';
const borderColor = '#e1dcdc';

const blackColor = '#000';
const dotColor = '#CCDBEA';
const blackColors = ['1.00', '0.85', '0.65', '0.45', '0.24', '0.12', '0.06'].map(
	(value) => `rgba(${hexToRgb(blackColor)}, ${value})`
);

const whiteColor = '#FFF';
const twitterColor = '#55acee';
const facebookColor = '#3b5998';
const googleColor = '#dd4b39';
const linkedinColor = '#0976b4';
const pinterestColor = '#cc2127';
const youtubeColor = '#e52d27';
const tumblrColor = '#35465c';
const behanceColor = '#1769ff';
const dribbbleColor = '#ea4c89';
const redditColor = '#ff4500';
const grassColor = '#8dcb7e';
const skyColor = '#13d3ff';
const support1Color = ['#EA6E00 ', '#FFBC80'];
const support2Color = ['#5F259F', '#984ccd'];
const support3Color = ['#5A5A5A'];
const primarySkyColor = '#0085CADE';
const bannerPurpleColor = '#984CCD';
const bannerPinkColor = '#E90091';
const activeColor = '#3DE8AC';
const pendingColor = '#FF9333';
const cardBackground = '#E5EDF5';
const greyColor = '#CCCC';
const discount = '#FFD7B3';
const infoIconGrayColor = '#8f8585';
const boxShadow = {
	boxShadow:
		'0 10px 30px -12px rgba(' +
		hexToRgb(blackColor) +
		', 0.42), 0 4px 25px 0px rgba(' +
		hexToRgb(blackColor) +
		', 0.12), 0 8px 10px -5px rgba(' +
		hexToRgb(blackColor) +
		', 0.2)',
};

const grayBoxShadow = {
	boxShadow: '0 6px 10px -3px rgba(' + hexToRgb(blackColor) + ', 0.18)',
};

const primaryBoxShadow = {
	boxShadow:
		'0 4px 4px 0 rgba(' +
		hexToRgb(blackColor) +
		',.14), 0 7px 10px -5px rgba(' +
		hexToRgb(primaryColor[2]) +
		',.4)',
};
const infoBoxShadow = {
	boxShadow:
		'0 4px 20px 0 rgba(' + hexToRgb(blackColor) + ',.14), 0 7px 10px -5px rgba(' + hexToRgb(infoColor[0]) + ',.4)',
};
const successBoxShadow = {
	boxShadow:
		'0 4px 20px 0 rgba(' +
		hexToRgb(blackColor) +
		',.14), 0 7px 10px -5px rgba(' +
		hexToRgb(successColor[0]) +
		',.4)',
};
const warningBoxShadow = {
	boxShadow:
		'0 4px 20px 0 rgba(' +
		hexToRgb(blackColor) +
		',.14), 0 7px 10px -5px rgba(' +
		hexToRgb(warningColor[0]) +
		',.4)',
};
const dangerBoxShadow = {
	boxShadow:
		'0 4px 20px 0 rgba(' +
		hexToRgb(blackColor) +
		',.14), 0 7px 10px -5px rgba(' +
		hexToRgb(dangerColor[0]) +
		',.4)',
};
const roseBoxShadow = {
	boxShadow:
		'0 4px 20px 0 rgba(' + hexToRgb(blackColor) + ',.14), 0 7px 10px -5px rgba(' + hexToRgb(roseColor[0]) + ',.4)',
};

const warningCardHeader = {
	background: warningColor[0],
};
const successCardHeader = {
	background: successColor[0],
};
const dangerCardHeader = {
	background: dangerColor[0],
};
const infoCardHeader = {
	background: infoColor[0],
};
const primaryCardHeader = {
	background: primaryColor[0],
};
const roseCardHeader = {
	background: roseColor[0],
};
const whiteCardHeader = {
	background: whiteColor,
	...grayBoxShadow,
};
const card = {
	display: 'inline-block',
	position: 'relative',
	width: '100%',
	margin: '25px 0',
	boxShadow: '0 1px 4px 0 rgba(' + hexToRgb(blackColor) + ', 0.14)',
	borderRadius: '6px',
	color: 'rgba(' + hexToRgb(blackColor) + ', 0.87)',
	background: whiteColor,
};

const gradients = {
	error: { background: 'linear-gradient( #e90091, #ce0058 )' },
	support1: { background: 'linear-gradient( #984ccd, #5f259f )' },
	primary: { background: 'linear-gradient( #FFBC80, #FF7800 )' },
	success: { background: 'linear-gradient( #00d5f4, #00bfb3 )' },
	donate: { background: 'linear-gradient( #ffcd3a, #FFD7B3 )' },
};

const defaultBoxShadow = {
	border: '0',
	borderRadius: '3px',
	boxShadow:
		'0 10px 20px -12px rgba(' +
		hexToRgb(blackColor) +
		', 0.42), 0 3px 20px 0px rgba(' +
		hexToRgb(blackColor) +
		', 0.12), 0 8px 10px -5px rgba(' +
		hexToRgb(blackColor) +
		', 0.2)',
	padding: '10px 0',
	transition: 'all 150ms ease 0s',
};

const theme = {
	palette: {
		primary: {
			main: primaryColor[0],
		},
	},
};

const browser = {
	IE: '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)',
	Edge: '@supports (-ms-accelerator:true) or (-ms-ime-align:auto)',
};

const createTheme = (direction: string, language: string, theme: any) =>
	createMuiTheme({
		direction,
		language,
		fontFamily: fontFamily(language),
		browser,
		fontSize: (size: number) => fontSize(language, size),
		breakpoints: {
			values: {
				xs: 0,
				sm: 576,
				md: 768,
				lg: 992,
				xl: 1200,
			},
		},
		...theme,
	});
const dark_blue = '#00000026';
const dark_grey = '#808080';
const light_grey = '#F3F2F24D';
const light_grey_1 = '#D1D1D1';
const light_grey_2 = '0000008a';
const dark_grey_1 = '#9E9E9E';
const light_green = '#49D16F';

const blue_1 = '#0079B8';
const lightGreenShade_1 = '#E7F8E7';
const lightGreenShade_2 = '#00D389';
const lightOrange = '#FFF4E9';
const orangeShade1 = '#FF8F27';
const lightGreenColor = '#00BFB3';
const headerBorderColor = '#dee2e6';
const menuHoverBgColor = '#fff2e6';
const menuBoxShadowColor = '#e9ecef';
const iconHoverFilter =
	'brightness(0) saturate(100%) invert(54%) sepia(47%) saturate(6201%) hue-rotate(9deg) brightness(100%) contrast(101%)';
const formElementBaseBgColor = '#EA6E00';
const avatarColors = {
	bg: '#E6F3FA',
	text: '#005E8F',
};
const profileSupportColor = '#797979';
const formButtons = [
	formElementBaseBgColor,
	'#FF7800',
	'#B55500',
	'#F1F1F1',
]; /* link, hover, active, disabled colors */
const formElementColors = {
	label: '#A4A4A4',
	bg: '#FCFCFC',
	error: '#F02B02',
};

export {
	hexToRgb,
	//variables
	drawerWidth,
	drawerMiniWidth,
	transition,
	container,
	containerFluid,
	boxShadow,
	card,
	defaultFont,
	defaultBodyFont,
	primaryColor,
	secondaryColor,
	warningColor,
	dangerColor,
	successColor,
	infoColor,
	roseColor,
	grayColor,
	blackColor,
	blackColors,
	whiteColor,
	support1Color,
	support2Color,
	support3Color,
	twitterColor,
	facebookColor,
	googleColor,
	linkedinColor,
	pinterestColor,
	youtubeColor,
	tumblrColor,
	behanceColor,
	dribbbleColor,
	redditColor,
	primaryBoxShadow,
	infoBoxShadow,
	successBoxShadow,
	warningBoxShadow,
	dangerBoxShadow,
	roseBoxShadow,
	warningCardHeader,
	successCardHeader,
	dangerCardHeader,
	whiteCardHeader,
	infoCardHeader,
	primaryCardHeader,
	roseCardHeader,
	defaultBoxShadow,
	grayBoxShadow,
	theme,
	grassColor,
	skyColor,
	createTheme,
	backgroundColor,
	gradients,
	rowHoverColor,
	infoColorLight,
	orangeLabelColor,
	selectedRowColor,
	unSelectedRowColor,
	placeholderColor,
	borderColor,
	primarySkyColor,
	activeColor,
	pendingColor,
	cardBackground,
	discount,
	dotColor,
	lilacColor,
	lightGreenShade_1,
	lightGreenShade_2,
	lightOrange,
	orangeShade1,
	grayColorShade_1,
	grayColorShade_2,
	grayColorShade_3,
	bannerPurpleColor,
	infoIconGrayColor,
	greyColor,
	dark_blue,
	dark_grey,
	light_grey,
	light_grey_1,
	light_green,
	light_grey_2,
	dark_grey_1,
	blue_1,
	grayColorShade_4,
	grayColorShade_5,
	lightGreenColor,
	bannerPinkColor,
	headerBorderColor,
	menuHoverBgColor,
	menuBoxShadowColor,
	iconHoverFilter,
	formElementBaseBgColor,
	avatarColors,
	profileSupportColor,
	formButtons,
	formElementColors,
};

export const colorNeutrals200 = '#bababa';
export const colorSupportRed600 = '#DA2702';
export const $colorNeutrals100 = '#d0d0d0';
export const colorSupportRed200 = '#f89d8b';
